import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Navigation, MapPin, X, Play, Square, AlertCircle, Car, Clock, Target } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "@/hooks/use-toast";
import { routeTrackingService } from "@/services/routeTrackingService";

interface RouteTrackingWidgetProps {
  hostUserId: string;
  hostUserName: string;
  destinationAddress: string;
  addressShareId?: string;
  onClose: () => void;
}

const RouteTrackingWidget = ({ 
  hostUserId, 
  hostUserName, 
  destinationAddress, 
  addressShareId,
  onClose 
}: RouteTrackingWidgetProps) => {
  const { user } = useAuth();
  const [isTracking, setIsTracking] = useState(false);
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [destinationCoords, setDestinationCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [distance, setDistance] = useState<number | null>(null);
  const [eta, setEta] = useState<Date | null>(null);
  const [routeTrackingId, setRouteTrackingId] = useState<string | null>(null);
  const [hasArrived, setHasArrived] = useState(false);
  const locationWatchId = useRef<number | null>(null);

  // Geocode the destination address to get coordinates
  useEffect(() => {
    const geocodeDestination = async () => {
      try {
        // Use a geocoding service (this is a simplified example)
        // In production, you'd use Google Maps Geocoding API, MapBox, etc.
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(destinationAddress)}`
        );
        const data = await response.json();
        
        if (data && data.length > 0) {
          setDestinationCoords({
            lat: parseFloat(data[0].lat),
            lng: parseFloat(data[0].lon)
          });
        }
      } catch (error) {
        console.error('Geocoding failed:', error);
        toast({
          title: "Address Lookup Failed",
          description: "Could not find coordinates for the destination address",
          variant: "destructive",
        });
      }
    };

    if (destinationAddress) {
      geocodeDestination();
    }
  }, [destinationAddress]);

  // Calculate distance and ETA when locations change
  useEffect(() => {
    if (currentLocation && destinationCoords) {
      const dist = routeTrackingService.calculateDistance(
        currentLocation.lat, 
        currentLocation.lng, 
        destinationCoords.lat, 
        destinationCoords.lng
      );
      setDistance(dist);
      
      // Calculate ETA (assuming 5 km/h walking speed)
      const estimatedEta = routeTrackingService.calculateETA(
        currentLocation.lat, 
        currentLocation.lng, 
        destinationCoords.lat, 
        destinationCoords.lng
      );
      setEta(estimatedEta);

      // Check if arrived (within 100m)
      if (dist < 0.1 && isTracking) {
        handleArrival();
      }
    }
  }, [currentLocation, destinationCoords, isTracking]);

  const handleArrival = async () => {
    setHasArrived(true);
    
    if (routeTrackingId) {
      try {
        await routeTrackingService.completeRouteTracking(routeTrackingId);
      } catch (error) {
        console.error('Error completing route tracking:', error);
      }
    }
    
    stopLocationTracking();
    
    toast({
      title: "🎉 You've arrived!",
      description: "Route tracking has been completed automatically.",
    });
  };

  const startRouteTracking = async () => {
    if (!user?.id || !destinationCoords) {
      toast({
        title: "Setup Required",
        description: "User authentication and destination coordinates are required",
        variant: "destructive",
      });
      return;
    }

    if (!navigator.geolocation) {
      toast({
        title: "Geolocation Not Supported",
        description: "Your browser doesn't support location tracking",
        variant: "destructive",
      });
      return;
    }

    try {
      // Get initial location
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 30000
        });
      });

      const initialLocation = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };
      
      setCurrentLocation(initialLocation);

      // Calculate initial distance and duration
      const initialDistance = routeTrackingService.calculateDistance(
        initialLocation.lat, 
        initialLocation.lng, 
        destinationCoords.lat, 
        destinationCoords.lng
      );

      // Start route tracking in database
      const routeTracking = await routeTrackingService.startRouteTracking({
        guestUserId: user.id,
        hostUserId: hostUserId,
        addressShareId: addressShareId,
        currentLatitude: initialLocation.lat,
        currentLongitude: initialLocation.lng,
        destinationLatitude: destinationCoords.lat,
        destinationLongitude: destinationCoords.lng,
        destinationAddress: destinationAddress,
        totalDistanceKm: initialDistance,
        estimatedDurationMinutes: Math.round((initialDistance / 5) * 60), // 5 km/h walking speed
      });

      setRouteTrackingId(routeTracking.id);

      // Start watching position
      locationWatchId.current = navigator.geolocation.watchPosition(
        async (pos) => {
          const newLocation = {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude
          };
          
          setCurrentLocation(newLocation);

          // Update location in database
          try {
            await routeTrackingService.updateLocation(
              routeTracking.id,
              newLocation.lat,
              newLocation.lng,
              {
                accuracy: pos.coords.accuracy,
                heading: pos.coords.heading || undefined,
                speed: pos.coords.speed || undefined,
              }
            );
          } catch (error) {
            console.error('Error updating location:', error);
          }
        },
        (error) => {
          console.error('Location tracking error:', error);
          toast({
            title: "Location Error",
            description: "Failed to track location. Please check your GPS.",
            variant: "destructive",
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 30000
        }
      );

      setIsTracking(true);
      
      toast({
        title: "Route Tracking Started",
        description: `${hostUserName} can now see your live journey progress`,
      });

    } catch (error) {
      console.error('Failed to start route tracking:', error);
      toast({
        title: "Permission Denied",
        description: "Please allow location access to share your live route",
        variant: "destructive",
      });
    }
  };

  const stopLocationTracking = () => {
    if (locationWatchId.current !== null) {
      navigator.geolocation.clearWatch(locationWatchId.current);
      locationWatchId.current = null;
    }
  };

  const handleStopTracking = async () => {
    stopLocationTracking();
    
    if (routeTrackingId) {
      try {
        await routeTrackingService.cancelRouteTracking(routeTrackingId);
      } catch (error) {
        console.error('Error stopping route tracking:', error);
      }
    }
    
    setIsTracking(false);
    setCurrentLocation(null);
    setDistance(null);
    setEta(null);
    setRouteTrackingId(null);
    
    toast({
      title: "Route Tracking Stopped",
      description: "Your live route is no longer being shared",
    });
  };

  const openInMaps = () => {
    if (currentLocation && destinationCoords) {
      const url = `https://www.google.com/maps/dir/${currentLocation.lat},${currentLocation.lng}/${destinationCoords.lat},${destinationCoords.lng}`;
      window.open(url, '_blank');
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopLocationTracking();
    };
  }, []);

  const formatETA = (eta: Date | null) => {
    if (!eta) return "Calculating...";
    const now = new Date();
    const diff = eta.getTime() - now.getTime();
    const minutes = Math.round(diff / (1000 * 60));
    return `${minutes} min${minutes !== 1 ? 's' : ''}`;
  };

  const formatDistance = (dist: number | null) => {
    if (!dist) return "Calculating...";
    if (dist < 1) return `${Math.round(dist * 1000)}m`;
    return `${dist.toFixed(1)}km`;
  };

  return (
    <Card className="mt-4 bg-carbon/90 border-electric-violet backdrop-blur-glass animate-fade-in">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-ice-white">
            <Navigation className="w-5 h-5 text-electric-violet" />
            Live Route to {hostUserName}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-ice-white/60 hover:text-ice-white"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {!isTracking ? (
          <>
            <div className="p-3 bg-electric-violet/10 border border-electric-violet/30 rounded-lg">
              <div className="flex items-start gap-2 text-sm text-ice-white/80">
                <AlertCircle className="w-4 h-4 text-electric-violet mt-0.5 flex-shrink-0" />
                <div>
                  <strong className="text-electric-violet">Live Route Navigation</strong>
                  <br />
                  Share your real-time journey as you travel to the destination. The host will see your live progress on a map.
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm text-ice-white/70">
                <Target className="w-4 h-4" />
                <span>Destination: {destinationAddress}</span>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-ice-white/70">
                <MapPin className="w-4 h-4" />
                <span>Your route will be visible to {hostUserName}</span>
              </div>
              
              <div className="flex items-center gap-2 text-sm text-ice-white/70">
                <Navigation className="w-4 h-4" />
                <span>Auto-stops when you arrive at destination</span>
              </div>
            </div>

            <Button
              onClick={startRouteTracking}
              className="w-full bg-electric-violet hover:bg-electric-violet/80 text-ice-white font-semibold"
              disabled={!destinationCoords}
            >
              <Play className="w-4 h-4 mr-2" />
              Start Live Route Tracking
            </Button>
          </>
        ) : (
          <div className="space-y-4">
            <div className="p-4 bg-electric-violet/20 border border-electric-violet/30 rounded-lg">
              <div className="text-ice-white font-medium mb-2 flex items-center gap-2">
                <div className="w-2 h-2 bg-electric-violet rounded-full animate-pulse"></div>
                Live route tracking active
              </div>
              
              {currentLocation && (
                <div className="text-sm text-ice-white/70 space-y-1">
                  <div className="flex items-center gap-2">
                    <MapPin className="w-3 h-3" />
                    Distance: {formatDistance(distance)}
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="w-3 h-3" />
                    ETA: {formatETA(eta)}
                  </div>
                  <div className="flex items-center gap-2">
                    <Target className="w-3 h-3" />
                    Destination: {destinationAddress}
                  </div>
                </div>
              )}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <Button
                onClick={handleStopTracking}
                variant="destructive"
                className="flex-1"
              >
                <Square className="w-4 h-4 mr-2" />
                Stop Tracking
              </Button>
              
              <Button
                onClick={openInMaps}
                variant="outline"
                className="flex-1 border-ice-white/20 text-ice-white hover:bg-ice-white/10"
              >
                <Car className="w-4 h-4 mr-2" />
                Open Maps
              </Button>
            </div>

            {hasArrived && (
              <div className="p-3 bg-toxic-green/20 border border-toxic-green/30 rounded-lg">
                <div className="text-toxic-green font-medium text-sm">
                  🎉 You've arrived at your destination!
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default RouteTrackingWidget;